import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Alert,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Divider,
  Snackbar
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Pets as PetsIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import {
  StyledTableContainer,
  TableSkeleton,
  FilterBar,
  SearchInput,
  FilterSelect,
  StyledPagination,
  EmptyState
} from '../components/TableShared';
import { format } from 'date-fns';
import api from '../config/api';

const ff = "'Poppins', sans-serif";

// Product only supports Dog and Cat (matches the mobile Add Pet flow).
const PET_TYPES = ['All', 'Dog', 'Cat'];

export default function Pets() {
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPets, setTotalPets] = useState(0);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');

  // Detail dialog state
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedPet, setSelectedPet] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchPets = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      };
      if (search.trim()) {
        params.search = search.trim();
      }
      if (typeFilter !== 'All') {
        params.type = typeFilter;
      }
      const response = await api.get('/admin/pets', { params });
      const data = response.data.data || response.data;
      setPets(data.pets || []);
      setTotalPets(data.total || data.pets?.length || 0);
    } catch (error) {
      console.error('Error fetching pets:', error);
      setError('Failed to load pets. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, typeFilter]);

  useEffect(() => {
    fetchPets();
  }, [fetchPets]);

  // Debounce search: reset page when search changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTypeFilterChange = (event) => {
    setTypeFilter(event.target.value);
    setPage(0);
  };

  const handleViewClick = (pet) => {
    setSelectedPet(pet);
    setDetailDialogOpen(true);
  };

  const handleDetailClose = () => {
    setDetailDialogOpen(false);
    setSelectedPet(null);
  };

  const handleDeletePet = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/admin/pets/${deleteTarget.id}`);
      setSnackbar({ open: true, message: 'Pet deleted', severity: 'success' });
      setDeleteTarget(null);
      fetchPets();
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to delete pet', severity: 'error' });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return 'N/A';
    }
  };

  const getTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'dog': return 'primary';
      case 'cat': return 'secondary';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ fontFamily: ff }}>
      {/* Filters Row */}
      <FilterBar>
        <SearchInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or breed..."
        />
        <FilterSelect
          label="Type"
          value={typeFilter}
          onChange={handleTypeFilterChange}
          options={PET_TYPES}
        />
      </FilterBar>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <TableSkeleton columns={8} rows={6} />
      ) : (
        <StyledTableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Photo</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Breed</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>QR Code</TableCell>
                <TableCell>Registered</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pets.map((pet) => (
                <TableRow key={pet.id}>
                  <TableCell>
                    <Avatar
                      src={pet.photoUrl}
                      alt={pet.name}
                      sx={{ width: 40, height: 40 }}
                    >
                      <PetsIcon fontSize="small" />
                    </Avatar>
                  </TableCell>
                  <TableCell>{pet.name || 'N/A'}</TableCell>
                  <TableCell>
                    <Chip
                      label={pet.type || 'N/A'}
                      color={getTypeColor(pet.type)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{pet.breed || 'N/A'}</TableCell>
                  <TableCell>
                    {pet.user
                      ? pet.user.fullName || pet.user.email
                      : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={pet.qrCode ? 'Yes' : 'No'}
                      color={pet.qrCode ? 'success' : 'default'}
                      size="small"
                      variant={pet.qrCode ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>{formatDate(pet.createdAt)}</TableCell>
                  <TableCell>
                    <IconButton size="small" color="primary" onClick={() => handleViewClick(pet)}>
                      <ViewIcon />
                    </IconButton>
                    <IconButton size="small" sx={{ color: '#E53E3E' }} onClick={() => setDeleteTarget(pet)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {pets.length === 0 && (
                <EmptyState colSpan={8} message="No pets found." />
              )}
            </TableBody>
          </Table>
          <StyledPagination
            count={totalPets}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </StyledTableContainer>
      )}

      {/* Pet Detail Dialog */}
      <Dialog open={detailDialogOpen} onClose={handleDetailClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: ff }}>Pet Details</DialogTitle>
        <DialogContent>
          {selectedPet && (
            <Box sx={{ pt: 1, fontFamily: ff }}>
              {/* Pet Photo and Name */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar
                  src={selectedPet.photoUrl}
                  alt={selectedPet.name}
                  sx={{ width: 72, height: 72 }}
                >
                  <PetsIcon />
                </Avatar>
                <Box>
                  <Box sx={{ fontSize: 18, fontWeight: 600 }}>{selectedPet.name || 'N/A'}</Box>
                  <Chip
                    label={selectedPet.type || 'N/A'}
                    color={getTypeColor(selectedPet.type)}
                    size="small"
                  />
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Pet Info */}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ fontSize: 12, color: '#718096' }}>Breed</Box>
                  <Box sx={{ fontSize: 14 }}>{selectedPet.breed || 'N/A'}</Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ fontSize: 12, color: '#718096' }}>Sex</Box>
                  <Box sx={{ fontSize: 14 }}>{selectedPet.sex || 'N/A'}</Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ fontSize: 12, color: '#718096' }}>Age</Box>
                  <Box sx={{ fontSize: 14 }}>
                    {selectedPet.ageYears != null || selectedPet.ageMonths != null
                      ? `${selectedPet.ageYears || 0}y ${selectedPet.ageMonths || 0}m`
                      : 'N/A'}
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ fontSize: 12, color: '#718096' }}>Weight</Box>
                  <Box sx={{ fontSize: 14 }}>
                    {selectedPet.weight != null ? `${selectedPet.weight} kg` : 'N/A'}
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ fontSize: 12, color: '#718096' }}>QR Code</Box>
                  <Chip
                    label={selectedPet.qrCode ? 'Linked' : 'Not linked'}
                    color={selectedPet.qrCode ? 'success' : 'default'}
                    size="small"
                    variant={selectedPet.qrCode ? 'filled' : 'outlined'}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ fontSize: 12, color: '#718096' }}>Registered</Box>
                  <Box sx={{ fontSize: 14 }}>{formatDate(selectedPet.createdAt)}</Box>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Owner Info */}
              <Box sx={{ fontSize: 13, fontWeight: 600, color: '#718096', mb: 1 }}>
                Owner Information
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box sx={{ fontSize: 12, color: '#718096' }}>Name</Box>
                  <Box sx={{ fontSize: 14 }}>
                    {selectedPet.user?.fullName || 'N/A'}
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ fontSize: 12, color: '#718096' }}>Email</Box>
                  <Box sx={{ fontSize: 14 }}>
                    {selectedPet.user?.email || 'N/A'}
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ fontSize: 12, color: '#718096' }}>Phone</Box>
                  <Box sx={{ fontSize: 14 }}>
                    {selectedPet.user?.phone || 'N/A'}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDetailClose} sx={{ fontFamily: ff }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs">
        <DialogTitle sx={{ fontFamily: ff, fontWeight: 600 }}>Delete Pet?</DialogTitle>
        <DialogContent>
          <Box sx={{ fontFamily: ff, fontSize: 13, color: '#718096' }}>
            This will permanently remove "{deleteTarget?.name}" and all associated data.
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} sx={{ fontFamily: ff, textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleDeletePet} variant="contained" color="error" sx={{ fontFamily: ff, textTransform: 'none' }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar(p => ({ ...p, open: false }))}>
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
